import './ProgramOverview.scss';

import { Container } from 'react-bootstrap';
import { faCoins, faHeart, faShieldHalved, faUserDoctor } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MarketingSectionIntro } from '@components/marketing/MarketingSectionIntro/MarketingSectionIntro';
import { ResourceGridBlock } from '@components/marketing/ResourceGrid/ResourceGrid';
import type { ResourceGridItem } from '@components/marketing/ResourceGrid/ResourceGrid.types';

const PROGRAM_RESOURCES: ResourceGridItem[] = [
  {
    slug: 'hypopara-assoc',
    kind: 'Web · External',
    title: 'HypoPARAthyroidism Association (HPA)',
    description:
      'A patient-led organization that helps people and families living with hypoparathyroidism find information, support, and healthcare providers in their area.',
    ctaLabel: 'Visit',
    icon: faHeart,
  },
];

export const ProgramOverview = () => (
  <section className='fvt-program marketing-section' aria-labelledby='fvt-program-heading'>
    <Container fluid='xxl'>
      <MarketingSectionIntro
        variant='fvt'
        eyebrow='Program overview'
        heading='What is Family Cascade Genetic Testing?'
        headingId='fvt-program-heading'
      >
        <p>
          Each eligible blood relative will receive no-charge genetic testing for the same <i>CASR</i> gene change
          (variant) and have the opportunity to speak with a genetic counselor to understand their genetic testing
          results.
        </p>
      </MarketingSectionIntro>

      <div className='fvt-program__grid'>
        <div className='fvt-program__col'>
          <h3>How the program works</h3>
          <p>Provide your relative’s name and email, and MyOme will send them a private invitation.</p>
          <p>
            The invitation will not include your name, medical information, or genetic test results. Your relative can
            review the information and decide for themselves whether they want to move forward.
          </p>
          <p>
            If they do, MyOme ships a cheek swab kit, runs a family variant genetic test, and connects them with a
            genetic counselor to walk through what the result means.
          </p>
          <ul className='fvt-program__bullets'>
            <li>
              <FontAwesomeIcon icon={faShieldHalved} aria-hidden='true' />
              <span>
                <strong>Private to them</strong>
                Each invitation is theirs alone. They choose what to do, on their own timeline.
              </span>
            </li>
            <li>
              <FontAwesomeIcon icon={faCoins} aria-hidden='true' />
              <span>
                <strong>No charge for eligible relatives</strong>
                The test, the kit, and the counselor session are covered.
              </span>
            </li>
            <li>
              <FontAwesomeIcon icon={faUserDoctor} aria-hidden='true' />
              <span>
                <strong>Counselor-supported</strong>
                Every relative who opts in is offered a session with a board-certified genetic counselor.
              </span>
            </li>
          </ul>
        </div>

        <div className='fvt-program__col'>
          <h3>Who it&apos;s for</h3>
          <p>
            For this genetic testing, eligibility is limited to family members who are related to you by blood, as this
            condition can be inherited. Spouses, step-family, and adoptive family members aren’t eligible for this
            genetic test. We recognize how important they are in your life, and we’re here to support you in talking
            with them about your diagnosis and genetic testing when you’re ready.
          </p>
        </div>
      </div>

      <ResourceGridBlock
        variant='fvt'
        heading='Resources'
        lead='Things you can read now, or download and share with your family.'
        items={PROGRAM_RESOURCES}
        links={{ 'hypopara-assoc': 'https://www.hypopara.org/' }}
      />
    </Container>
  </section>
);
